type LogMethod = 'log' | 'info' | 'warn' | 'groupCollapsed'

type PillSegment = {
  text: string
  style: string
}

const PILL_STYLES = {
  gray: 'background: light-dark(#e0e0e0, #333); color: light-dark(#333, #e0e0e0); font-weight: bold; padding: 2px;',
  light:
    'background: light-dark(#f5f5f5, #2a2a2a); color: light-dark(#666, #aaa); padding: 2px 8px 2px 0; border-radius: 0 4px 4px 0;',
} as const

function buildPillLog(
  emoji: string,
  segments: PillSegment[],
  method: LogMethod = 'log',
  ...data: unknown[]
) {
  const lastIndex = segments.length - 1
  const template = segments.map((s) => `%c ${s.text} `).join('')
  const styles = segments.map((s, i) => {
    let style = s.style
    if (i === 0) {
      style += ' border-radius: 4px 0 0 4px;'
    }
    if (i === lastIndex) {
      style += ' border-radius: 0 4px 4px 0;'
    }
    return style
  })

  console[method](`${emoji} ${template}`, ...styles, ...data)
}

// -- LaunchDarkly --

const LD_STYLE =
  'background: light-dark(#e0f2f1, #1a3a3a); color: light-dark(#00695c, #80cbc4); font-weight: bold; padding: 2px 0;'
const LD_WARN_STYLE =
  'background: light-dark(#fff3e0, #4a3210); color: light-dark(#f57c00, #ffcc80); padding: 2px 8px 2px 0;'

interface LdLogOptions {
  title: string
  label: string
  data?: unknown
  method?: LogMethod
}

export function ldLog({ title, label, data, method = 'log' }: LdLogOptions) {
  buildPillLog(
    '🏁',
    [
      { text: 'LaunchDarkly', style: LD_STYLE },
      { text: title, style: PILL_STYLES.gray },
      { text: label, style: PILL_STYLES.light },
    ],
    method,
    ...(data !== undefined ? [data] : []),
  )
}

export function ldLogWarn(message: string) {
  buildPillLog(
    '🏁',
    [
      { text: 'LaunchDarkly', style: LD_STYLE },
      { text: message, style: LD_WARN_STYLE },
    ],
    'warn',
  )
}

// -- i18next --

const I18N_STYLE =
  'background: light-dark(#e3f2fd, #1a2a3a); color: light-dark(#1565c0, #90caf9); font-weight: bold; padding: 2px 0;'

export const i18nLogger = {
  type: 'logger' as const,
  log(args: unknown[]) {
    this.output('log', args)
  },
  warn(args: unknown[]) {
    this.output('warn', args)
  },
  error(args: unknown[]) {
    this.output('error', args)
  },
  output(type: 'log' | 'warn' | 'error', args: unknown[]) {
    const [prefix, ...rest] = args
    const message =
      typeof prefix === 'string'
        ? prefix.replace('i18next:', '').trim()
        : prefix
    buildPillLog(
      '🌐',
      [
        { text: 'i18next', style: I18N_STYLE },
        { text: String(message), style: PILL_STYLES.light },
      ],
      'groupCollapsed',
    )
    if (rest.length) {
      console.log(...rest)
    }
    console.groupEnd()
  },
}

// -- Mirage --

const MIRAGE_STYLE =
  'background: light-dark(#e8eaf6, #1a1a3a); color: light-dark(#3949ab, #9fa8da); font-weight: bold; padding: 2px 0;'

const STATUS_STYLES = {
  success:
    'background: light-dark(#ebf9eb, #1a3a1a); color: light-dark(#1d8633, #81c784);',
  redirect:
    'background: light-dark(#fff3e0, #4a3210); color: light-dark(#f57c00, #ffcc80);',
  error:
    'background: light-dark(#fce4ec, #4a1525); color: light-dark(#c2185b, #ef9a9a);',
} as const

function getStatusStyle(status: number) {
  if (status >= 400) {
    return STATUS_STYLES.error
  }
  if (status >= 300) {
    return STATUS_STYLES.redirect
  }
  return STATUS_STYLES.success
}

export function mirageLog(verb: string, status: number, shortPath: string) {
  console.groupCollapsed(
    `🏔️ %c Mirage %c ${verb} %c ${status} %c ${shortPath} `,
    MIRAGE_STYLE + ' border-radius: 4px 0 0 4px;',
    PILL_STYLES.gray,
    `${getStatusStyle(status)} font-weight: bold; padding: 2px;`,
    PILL_STYLES.light,
  )
}
